import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Package, Library, BookOpen, ArrowRight, Shield, ShieldCheck, ShieldOff, Copy, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TwoFactorSection = ({ user, token }) => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.two_factor_enabled || false);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [secret, setSecret] = useState('');
  const [qrUri, setQrUri] = useState('');
  const [step, setStep] = useState('password');
  const [loading, setLoading] = useState(false);

  const handleEnableStart = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/enable-2fa`, { password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecret(res.data.secret);
      setQrUri(res.data.qr_uri);
      setStep('verify');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    }
    setLoading(false);
  };

  const handleEnableConfirm = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/confirm-2fa`, { totp_code: totpCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIs2FAEnabled(true);
      setShowEnableDialog(false);
      resetState();
      toast.success('Two-factor authentication enabled!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/disable-2fa`, { totp_code: totpCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIs2FAEnabled(false);
      setShowDisableDialog(false);
      resetState();
      toast.success('Two-factor authentication disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  const resetState = () => {
    setPassword('');
    setTotpCode('');
    setSecret('');
    setQrUri('');
    setStep('password');
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copied to clipboard');
  };

  return (
    <Card className="border-pink-100/60 rounded-xl" data-testid="2fa-section">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full ${is2FAEnabled ? 'bg-green-50' : 'bg-muted'}`}>
              {is2FAEnabled ? (
                <ShieldCheck className="h-5 w-5 text-green-600" strokeWidth={1.5} />
              ) : (
                <Shield className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              )}
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground">
                {is2FAEnabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
              </p>
            </div>
          </div>
          {is2FAEnabled ? (
            <Button
              variant="outline"
              className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/5"
              onClick={() => { resetState(); setShowDisableDialog(true); }}
              data-testid="disable-2fa-btn"
            >
              <ShieldOff className="h-4 w-4 mr-2" />
              Disable
            </Button>
          ) : (
            <Button
              className="rounded-full"
              onClick={() => { resetState(); setShowEnableDialog(true); }}
              data-testid="enable-2fa-btn"
            >
              <Shield className="h-4 w-4 mr-2" />
              Enable 2FA
            </Button>
          )}
        </div>
      </CardContent>

      {/* Enable 2FA Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={(open) => { if (!open) resetState(); setShowEnableDialog(open); }}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Enable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              {step === 'password' ? 'Confirm your password to proceed' : 'Scan the QR code with your authenticator app'}
            </DialogDescription>
          </DialogHeader>

          {step === 'password' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="2fa-password-input"
                />
              </div>
              <Button onClick={handleEnableStart} disabled={loading || !password} className="w-full rounded-full" data-testid="2fa-password-submit">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Continue
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="text-center p-4 bg-white rounded-xl border border-pink-100/60">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`}
                  alt="2FA QR Code"
                  className="mx-auto w-48 h-48"
                  data-testid="2fa-qr-code"
                />
              </div>

              {/* Manual entry */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Or enter this code manually:</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono break-all" data-testid="2fa-secret">{secret}</code>
                  <Button variant="ghost" size="icon" onClick={copySecret} className="rounded-full shrink-0">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Verify code */}
              <div className="space-y-2">
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="text-center text-xl tracking-widest font-mono"
                  data-testid="2fa-verify-code"
                />
              </div>
              <Button onClick={handleEnableConfirm} disabled={loading || totpCode.length !== 6} className="w-full rounded-full" data-testid="2fa-verify-submit">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Verify & Enable
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={(open) => { if (!open) resetState(); setShowDisableDialog(open); }}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>Enter your authenticator code to disable 2FA</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Authenticator Code</Label>
              <Input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="text-center text-xl tracking-widest font-mono"
                data-testid="2fa-disable-code"
              />
            </div>
            <Button
              onClick={handleDisable}
              disabled={loading || totpCode.length !== 6}
              variant="destructive"
              className="w-full rounded-full"
              data-testid="2fa-disable-submit"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Disable 2FA
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({ orders: 0, library: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const config = { headers, withCredentials: true };

        const [ordersRes, libraryRes] = await Promise.all([
          axios.get(`${API}/orders`, config),
          axios.get(`${API}/orders/library`, config)
        ]);

        const orders = ordersRes.data.orders || [];
        const library = libraryRes.data.books || [];

        setStats({ orders: orders.length, library: library.length });
        setRecentOrders(orders.slice(0, 3));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="user-dashboard">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold mb-2" style={{color: 'hsl(340, 30%, 25%)'}}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">Here's an overview of your account</p>
        </div>

        {/* Email Verification Banner */}
        {user && !user.email_verified && (
          <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl flex items-center justify-between" data-testid="verify-email-banner">
            <div className="flex items-center gap-3">
              <MailCheck className="h-5 w-5 text-primary" />
              <p className="text-sm">Please verify your email address to access all features.</p>
            </div>
            <Button asChild size="sm" variant="outline" className="rounded-full border-primary/30">
              <Link to="/verify-email">Verify Now</Link>
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-pink-100/60 rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/8 rounded-full">
                  <Package className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.orders}</p>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-pink-100/60 rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/8 rounded-full">
                  <Library className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.library}</p>
                  <p className="text-sm text-muted-foreground">Books in Library</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-pink-100/60 rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/8 rounded-full">
                  <BookOpen className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <Button asChild variant="link" className="p-0 h-auto">
                    <Link to="/browse" className="text-lg font-semibold">
                      Browse Books
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <p className="text-sm text-muted-foreground">Discover new reads</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 2FA Section */}
        <div className="mb-8">
          <TwoFactorSection user={user} token={token} />
        </div>

        {/* Recent Orders */}
        <Card className="border-pink-100/60 rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl" style={{color: 'hsl(340, 30%, 25%)'}}>Recent Orders</CardTitle>
            <Button asChild variant="ghost" size="sm" className="rounded-full">
              <Link to="/orders">View All</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No orders yet</p>
                <Button asChild variant="outline" className="rounded-full">
                  <Link to="/browse">Start Shopping</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div
                    key={order.order_id}
                    className="flex items-center justify-between p-4 border border-pink-100/60 rounded-xl"
                  >
                    <div>
                      <p className="font-medium">Order #{order.order_id.slice(-8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.items.length} item{order.items.length > 1 ? 's' : ''} &bull; ${order.total.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'} className="rounded-full">
                        {order.payment_status}
                      </Badge>
                      <Button asChild variant="ghost" size="sm" className="rounded-full">
                        <Link to={`/orders/${order.order_id}`}>View</Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <Card className="border-pink-100/60 rounded-xl hover:shadow-soft transition-shadow">
            <Link to="/library">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-1">My Library</h3>
                    <p className="text-sm text-muted-foreground">Access your purchased books</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Link>
          </Card>
          <Card className="border-pink-100/60 rounded-xl hover:shadow-soft transition-shadow">
            <Link to="/orders">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-1">Order History</h3>
                    <p className="text-sm text-muted-foreground">View all your past orders</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
};
